import { useEffect, useState } from "react";
import ReactSelect, { Props as SelectProps } from "react-select";
import { ReactComponent as ChevronIcon } from "../../../../assets/svg/icons/chevronDown.svg";
import {
  IOptionProps,
  IOptionsProps,
} from "../../../../@types/reactSelect/IOption";
import { Controller } from "react-hook-form";
import { deepFind } from "../../../../utils/objects/deepFind";
import "./styles.css";

interface ReactSelectProps extends SelectProps<any> {
  name: string;
  getSelectedData?: (data: IOptionProps, action?: string) => void;
  control: any;
  required?: boolean;
  label?: string;
  placeholderText?: string;
  isSearchable?: boolean;
  errorText?: any;
  customOption?: React.ComponentType<any>;
  selectAll?: boolean;
  selectAllLabel?: string;
}

const mHeight = "300px";
const speed = "300ms";

export function Dropdown({
  name,
  options,
  getSelectedData,
  control,
  required = false,
  label,
  placeholderText,
  errorText,
  isSearchable = false,
  customOption,
  selectAll = false,
  selectAllLabel = "Selecionar Todos",
  ...rest
}: ReactSelectProps) {
  const [open, setOpen] = useState(rest.menuIsOpen ?? rest.defaultMenuIsOpen ?? false);
  const [selectOptions, setSelectOption] = useState<IOptionsProps>([]);
  const [upData, setUpData] = useState<boolean>(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [currentFieldValue, setCurrentFieldValue] = useState<any>(null);

  const selectStyles = () => ({
    option: (provided: any, state: any) => ({
      ...provided,
      position: "relative",
      background: "transparent",
      color: state.isSelected ? "#4D4D4D" : "#999999",
      textTransform: "captilize",
      fontWeight: "bold",
      opacity: open ? 1 : 0.5,
      transition: `all ${speed} ease-in-out`,
      padding: "0",
      paddingBottom: "20px",
      paddingLeft: "12px",
      "&:hover": {
        background: "transparent",
        color: "#4D4D4D",
        cursor: "pointer",
      },
      "&:last-child": {
        paddingBottom: "0",
      },
    }),
    control: (provided: any, { isDisabled }: any) => ({
      ...provided,
      borderRadius: !open ? "9px" : "9px 9px 0px 0px",
      background: rest.isDisabled ? "#F1F1F1" : "#FFF",
      outline: "none",
      minHeight: "56px",
      boxShadow: "none",
      border: `1px solid ${(errorText || !!fieldError) ? "#ff6161" : "#e5e5e5"} !important`,
      fontSize: 18,
      transition: `all ${speed} ease-in-out`,
      zIndex: 0,
      paddingLeft: "20px",
      paddingRight: "20px",
      cursor: isDisabled ? "not-allowed" : "default",
    }),
    menu: (provided: any) => ({
      ...provided,
      borderRadius: "0px 0px 9px 9px",
      outline: "none",
      boxShadow: "none",
      borderLeft: `1px solid ${(errorText || !!fieldError) ? "#ff6161" : "#e5e5e5"} !important`,
      borderRight: `1px solid ${(errorText || !!fieldError) ? "#ff6161" : "#e5e5e5"} !important`,
      borderBottom: `1px solid ${(errorText || !!fieldError) ? "#ff6161" : "#e5e5e5"} !important`,
      position: "relative",
      marginTop: "-10px",
      paddingTop: "10px",
      paddingLeft: "12px",
      paddingRight: "12px",
      marginBottom: "0px",
      fontSize: 18,
      maxHeight: open ? mHeight : "0px",
      overflow: "hidden",
      opacity: open ? 1 : 0.5,
      transition: `all ${speed} ease-in-out`,
      visibility: open ? "visible" : "hidden",
      animation: open
        ? `ease selectOpen ${speed} forwards`
        : `ease selectClose ${speed} forwards`,
      zIndex: 0,
    }),
    menuList: (defaultStyles: any) => ({
      ...defaultStyles,
      maxHeight: "270px",
    }),
    indicatorsContainer: (defaultStyles: any) => ({
      ...defaultStyles,
      transition: "ease-in-out 400ms",
      transform: open ? "rotateZ(0deg)" : "rotateZ(180deg)",
    }),
    input: (defaultStyles: any) => ({
      ...defaultStyles,
      margin: 0,
      padding: 0,
    }),
    valueContainer: (defaultStyles: any) => ({
      ...defaultStyles,
      padding: 0,
    }),
    noOptionsMessage: (defaultStyles: any) => ({
      ...defaultStyles,
      with: "100%",
      padding: "12px 0",
      margin: "0px",
    }),
  });

  useEffect(() => {
    if (rest.defaultMenuIsOpen) {
      setOpen(rest.defaultMenuIsOpen);
    }
  }, [rest.defaultMenuIsOpen]);

  useEffect(() => {
    if (rest.menuIsOpen !== undefined) {
      setOpen(rest.menuIsOpen);
    }
  }, [rest.menuIsOpen]);

  useEffect(() => {
    if (options && options.length > 0) {
      const mappedOptions = options.map((option: IOptionProps) => ({
        label: String(option?.label),
        value: String(option?.value),
        info: option?.info,
      }));

      // Verificar se todas as opções já estão selecionadas
      const selectedValues = currentFieldValue || [];
      const allSelected = Array.isArray(selectedValues) && 
                          mappedOptions.length > 0 &&
                          mappedOptions.every(option => 
                            selectedValues.some((selected: any) => selected?.value === option.value)
                          );

      // Adicionar opção "Selecionar Todos" apenas se:
      // 1. selectAll estiver ativo
      // 2. for multi-select
      // 3. houver mais de uma opção real disponível
      // 4. Nem todas as opções já estiverem selecionadas
      if (selectAll && rest.isMulti && mappedOptions.length > 1 && !allSelected) {
        const selectAllOption = {
          label: selectAllLabel,
          value: "__SELECT_ALL__",
          info: { isSelectAll: true }
        };
        setSelectOption([selectAllOption, ...mappedOptions]);
      } else {
        setSelectOption(mappedOptions);
      }
      setUpData(!upData);
    } else {
      setSelectOption([]);
    }
  }, [options, selectAll, selectAllLabel, rest.isMulti, currentFieldValue]);

  useEffect(() => {
    if (rest.isDisabled) {
      setOpen(false);
    }
  }, [rest.isDisabled]);

  // Sincronizar o valor do campo com o estado local quando o valor externo mudar
  useEffect(() => {
    if (rest.value !== undefined) {
      setCurrentFieldValue(rest.value);
    }
  }, [rest.value]);

  return (
    <>
      <div className={"dropdown__container"}>
        {label && <label>{label}</label>}
        <Controller
          name={name}
          control={control}
          render={({ field, formState: { errors } }) => {
            if (field.value !== currentFieldValue) {
              setCurrentFieldValue(field.value);
            }
            
            if (name.split('.').length > 1) {
              const error = deepFind(errors, name)
              setFieldError(!!error ? error.message as string : null)
            } else setFieldError(!!errors[name] ? errors[name]?.message as string : null)
            return (
              <ReactSelect
                {...rest}
                className={`react-select ${rest.className} ${errorText || !!fieldError ? "dropdown__container--invalid" : ""}`}
                options={selectOptions}
                menuIsOpen={rest.menuIsOpen ?? open}
                styles={selectStyles()}
                onMenuOpen={() => {
                  if (rest.menuIsOpen === undefined) {
                    setOpen(true);
                  }
                  rest.onMenuOpen && rest.onMenuOpen();
                }}
                onMenuClose={() => {
                  if (rest.menuIsOpen === undefined) {
                    setOpen(false);
                  }
                  rest.onMenuClose && rest.onMenuClose();
                }}
                components={customOption ? {
                  ...rest.components,
                  Option: customOption,
                  ClearIndicator: () => null,
                  IndicatorSeparator: () => null,
                  DropdownIndicator: () => <ChevronIcon />,
                } : {
                  ...rest.components,
                  ClearIndicator: () => null,
                  IndicatorSeparator: () => null,
                  DropdownIndicator: () => <ChevronIcon />,
                }}
                placeholder={placeholderText}
                isSearchable={isSearchable}
                {...field}
                onChange={(data, { action }) => {
                  // Atualizar o valor atual do campo no estado
                  setCurrentFieldValue(data);
                  
                  // Verificar se é uma seleção múltipla e se a opção "Selecionar Todos" foi clicada
                  if (rest.isMulti && Array.isArray(data)) {
                    const selectAllOption = data.find(item => item.value === "__SELECT_ALL__");
                    
                    if (selectAllOption) {
                      // Se "Selecionar Todos" foi clicada, selecionar/deselecionar todas as opções reais
                      const realOptions = selectOptions.filter(option => option.value !== "__SELECT_ALL__");
                      const currentValues = field.value || [];
                      
                      // Verificar se todas as opções reais estão selecionadas
                      const allSelected = realOptions.every(realOption => 
                        currentValues.some((selected: any) => selected.value === realOption.value)
                      );
                      
                      if (allSelected) {
                        // Se todas estão selecionadas, desmarcar todas
                        field.onChange([]);
                        setCurrentFieldValue([]);
                        getSelectedData && getSelectedData([] as any, action);
                      } else {
                        // Se não todas estão selecionadas, selecionar todas
                        field.onChange(realOptions);
                        setCurrentFieldValue(realOptions);
                        getSelectedData && getSelectedData(realOptions as any, action);
                      }
                    } else {
                      // Comportamento normal para outras opções
                      field.onChange(data);
                      getSelectedData && getSelectedData(data as any, action);
                    }
                  } else {
                    // Comportamento normal para seleção única
                    field.onChange(data);
                    getSelectedData && getSelectedData(data, action);
                  }
                  
                  if (action) setOpen(rest.closeMenuOnSelect ?? true);
                }}
              />
            )
          }}
        />
      </div>
      {(errorText || !!fieldError) && (
        <small className="textInput--small-error animation__fadeIn">
          {errorText ? errorText : fieldError}
        </small>
      )}
    </>
  );
}
